import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { inputCls, Label } from './sharedVendorUI';

/**
 * Dynamic per-state GST number table, shared by both vendor forms.
 * rows: [{ id, stateId, gstNo, checked }]
 */
const GSTDetailsTable = ({ rows, statesList, onAddRow, onRemoveRow, onRowChange, disabled }) => {
    const usedStateIds = rows.map((r) => r.stateId).filter(Boolean);

    return (
        <div>
            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30">
                            <th className="px-4 py-3 text-left text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase tracking-wider">State</th>
                            <th className="px-4 py-3 text-left text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase tracking-wider">GST Number</th>
                            <th className="px-4 py-3 text-center text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase tracking-wider">Verify</th>
                            <th className="px-4 py-3 text-center text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase tracking-wider">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, idx) => {
                            const availableStates = statesList.filter(
                                (s) => s.State_ID === row.stateId || !usedStateIds.includes(s.State_ID)
                            );
                            return (
                                <tr key={row.id} className={`border-t border-gray-100 dark:border-gray-700 ${idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50/50 dark:bg-gray-700/30'}`}>
                                    <td className="px-4 py-2">
                                        <select
                                            className={`${inputCls} appearance-none`}
                                            value={row.stateId}
                                            onChange={(e) => onRowChange(row.id, 'stateId', e.target.value)}
                                            disabled={disabled}
                                        >
                                            <option value="">-Please Select-</option>
                                            {availableStates.map((s) => (
                                                <option key={s.State_ID} value={s.State_ID}>{s.State_Name}</option>
                                            ))}
                                        </select>
                                    </td>
                                    <td className="px-4 py-2">
                                        <input
                                            type="text"
                                            className={inputCls}
                                            placeholder="Enter GST number"
                                            value={row.gstNo}
                                            onChange={(e) => onRowChange(row.id, 'gstNo', e.target.value)}
                                            disabled={disabled}
                                        />
                                    </td>
                                    <td className="px-4 py-2 text-center">
                                        <input
                                            type="checkbox"
                                            checked={!!row.checked}
                                            onChange={(e) => onRowChange(row.id, 'checked', e.target.checked)}
                                            disabled={disabled}
                                            className="accent-indigo-600 h-4 w-4"
                                        />
                                    </td>
                                    <td className="px-4 py-2 text-center">
                                        <button
                                            type="button"
                                            onClick={() => onRemoveRow(row.id)}
                                            disabled={disabled || rows.length === 1}
                                            className="p-1.5 rounded-lg text-rose-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors disabled:opacity-40"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <button
                type="button"
                onClick={onAddRow}
                disabled={disabled}
                className="mt-3 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-indigo-600 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition-colors disabled:opacity-50"
            >
                <Plus className="h-3.5 w-3.5" /> Add GST Row
            </button>
        </div>
    );
};

export default GSTDetailsTable;

export const newGSTRow = () => ({ id: Date.now() + Math.random(), stateId: '', gstNo: '', checked: false });

export const validateGSTRows = (rows) => {
    if (rows.length === 0) return 'Add at least one GST row.';
    for (const r of rows) {
        if (!r.stateId) return 'Select a state for every GST row.';
        if (!r.gstNo.trim()) return 'Enter the GST number for every row.';
        if (!r.checked) return 'Verify every GST row before submitting.';
    }
    const stateIds = rows.map((r) => r.stateId);
    if (new Set(stateIds).size !== stateIds.length) return 'Only one GST number is allowed per state.';
    return null;
};
